import { useState } from "react";
import { ApiError, type Workspace, type WorkspaceModules } from "@nahal/api-client";
import { Button, EmptyState, ErrorState, LoadingBlock, ModuleChecklist, Sheet, StatusBadge, TextAreaField } from "@nahal/ui";
import { useRetryWorkspaceModules, useSetWorkspaceModules, useWorkspaceModules } from "../../lib/queries";

export function ModuleGrantsSheet({
  organizationId, workspace, onClose,
}: {
  organizationId: string;
  workspace: Workspace;
  onClose: () => void;
}) {
  const query = useWorkspaceModules(workspace.externalRef ? workspace.id : "");
  return (
    <Sheet open title={"Modules for " + workspace.name} description="Choose the modules this workspace is entitled to use." onClose={onClose}>
      {!workspace.externalRef ? (
        <EmptyState title="Workspace is not linked" description="Finish provisioning this workspace before assigning modules." />
      ) : query.isFetching ? (
        <LoadingBlock label="Loading current module access" />
      ) : query.isError ? (
        <ErrorState
          message={query.error instanceof ApiError ? query.error.message : "Could not load module access."}
          action={<Button variant="outline" onClick={() => query.refetch()}>Try again</Button>}
        />
      ) : query.data ? (
        <ModuleGrantsForm
          key={String(query.data.grantRevision) + ":" + query.data.settingsRevision + ":" + query.data.syncStatus}
          state={query.data}
          workspaceId={workspace.id}
          organizationId={organizationId}
          onClose={onClose}
        />
      ) : <EmptyState title="No module access reported" />}
    </Sheet>
  );
}

function ModuleGrantsForm({
  state, workspaceId, organizationId, onClose,
}: {
  state: WorkspaceModules;
  workspaceId: string;
  organizationId: string;
  onClose: () => void;
}) {
  const [values, setValues] = useState<Record<string, boolean>>(() => Object.fromEntries(state.modules.map((module) => [module.id, module.granted])));
  const [reason, setReason] = useState("");
  const save = useSetWorkspaceModules(organizationId, workspaceId);
  const retry = useRetryWorkspaceModules(organizationId, workspaceId);
  const pending = save.isPending || retry.isPending;
  const changed = state.modules.some((module) => values[module.id] !== module.granted);
  const awaitingApplication = state.syncStatus === "PENDING" || state.syncStatus === "FAILED";

  if (state.modules.length === 0) {
    return <EmptyState title="No configurable modules" description="This product has not declared configurable modules." />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Workspace entitlement</h3>
        <StatusBadge
          status={state.syncStatus === "NOT_CONFIGURED" ? "Existing access" : state.syncStatus}
          tone={state.syncStatus === "APPLIED" ? "success" : state.syncStatus === "FAILED" ? "danger" : state.syncStatus === "PENDING" ? "warning" : "neutral"}
        />
      </div>
      {awaitingApplication && (
        <p role="status" className="text-sm text-[var(--text-secondary)]">
          The saved grants have not been confirmed by the product. Current access may differ until application succeeds.
        </p>
      )}
      {state.syncError && <ErrorState message={state.syncError} />}
      {!state.canManage && (
        <p className="text-sm text-[var(--text-secondary)]">You can view module access. An authorised provisioning operator must change the grants.</p>
      )}
      <ModuleChecklist
        mode="grants"
        items={state.modules}
        values={values}
        disabled={!state.canManage || pending}
        applied={!awaitingApplication}
        onChange={(id, checked) => setValues((current) => ({ ...current, [id]: checked }))}
      />
      {state.canManage && (
        <TextAreaField
          label="Reason for the change"
          hint="Recorded with the module grant history."
          required
          maxLength={500}
          value={reason}
          disabled={pending}
          onChange={(event) => setReason(event.target.value)}
        />
      )}
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
        <Button variant="outline" onClick={onClose} disabled={pending}>Close</Button>
        {state.canManage && awaitingApplication && (
          <Button variant="outline" disabled={pending || changed} onClick={() => retry.mutate(state.grantRevision)}>
            {retry.isPending ? "Retrying..." : "Retry application"}
          </Button>
        )}
        {state.canManage && (
          <Button
            variant="solid"
            disabled={pending || !changed || !reason.trim()}
            onClick={() => save.mutate({ expectedRevision: state.grantRevision, grants: values, reason: reason.trim() })}
          >
            {save.isPending ? "Saving..." : "Save grants"}
          </Button>
        )}
      </div>
    </div>
  );
}
